import { For, Show, createMemo, createSignal, type JSX } from "solid-js";
import {
  createSolidTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from "@tanstack/solid-table";

export type Column<T> = ColumnDef<T>;

export interface DataTableProps<T> {
  rows: T[];
  columns: Column<T>[];
  rowActions?: (row: T) => JSX.Element;
  emptyMessage?: string;
  globalFilter?: string;
  onRowActivate?: (row: T) => void;
  onRowSelect?: (row: T) => void;
  onRowContextMenu?: (row: T, event: MouseEvent) => void;
}

export function DataTable<T>(props: DataTableProps<T>): JSX.Element {
  const [sorting, setSorting] = createSignal<SortingState>([]);
  const [activeRowIndex, setActiveRowIndex] = createSignal<number>(-1);

  // Création de la table mémorisée pour éviter une recréation à chaque render.
  // data/columns/state restent réactifs grâce aux getters.
  const table = createMemo(() =>
    createSolidTable<T>({
      get data() {
        return props.rows;
      },
      get columns() {
        return props.columns;
      },
      state: {
        get sorting() {
          return sorting();
        },
        get globalFilter() {
          return props.globalFilter || "";
        },
      },
      onSortingChange: (updater) => {
        setSorting((prev) => (typeof updater === "function" ? updater(prev) : updater));
      },
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
    }),
  );

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!props.onRowActivate && !props.onRowSelect) return;

    const rows = table().getRowModel().rows;
    if (rows.length === 0) return;

    let newIndex = activeRowIndex();
    const lastIndex = rows.length - 1;

    switch (e.key) {
      case "ArrowUp":
        e.preventDefault();
        newIndex = Math.max(-1, newIndex - 1);
        break;
      case "ArrowDown":
        e.preventDefault();
        newIndex = Math.min(lastIndex, newIndex + 1);
        break;
      case "Home":
        e.preventDefault();
        newIndex = 0;
        break;
      case "End":
        e.preventDefault();
        newIndex = lastIndex;
        break;
      case "Enter":
        e.preventDefault();
        if (newIndex >= 0 && newIndex <= lastIndex && props.onRowActivate) {
          props.onRowActivate(rows[newIndex]!.original);
        }
        return;
      default:
        return;
    }

    setActiveRowIndex(newIndex);
    if (newIndex >= 0 && newIndex <= lastIndex && props.onRowSelect) {
      props.onRowSelect(rows[newIndex]!.original);
    }
  };

  return (
    <Show
      when={props.rows.length > 0}
      fallback={<div class="panel-data-empty">{props.emptyMessage || "Aucun resultat."}</div>}
    >
      <div
        class="data-table-container"
        tabindex={props.onRowActivate || props.onRowSelect ? 0 : -1}
        onKeyDown={handleKeyDown}
        style={{
          outline: "none",
        }}
      >
        <table class="panel-data-table">
          <thead>
            <For each={table().getHeaderGroups()}>
              {(hg) => (
                <tr>
                  <For each={hg.headers}>
                    {(h) => (
                      <th
                        onClick={h.column.getToggleSortingHandler()}
                        style={{ cursor: h.column.getCanSort() ? "pointer" : "default" }}
                      >
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        <Show when={h.column.getIsSorted()}>
                          <span> {h.column.getIsSorted() === "asc" ? "▲" : "▼"}</span>
                        </Show>
                      </th>
                    )}
                  </For>
                  <Show when={props.rowActions}>
                    <th>Actions</th>
                  </Show>
                </tr>
              )}
            </For>
          </thead>
          <tbody>
            <For each={table().getRowModel().rows}>
              {(row, idx) => (
                <tr
                  class={`data-table__row ${activeRowIndex() === idx() ? "data-table__row--active" : ""}`}
                  data-active={activeRowIndex() === idx() ? "true" : "false"}
                  onDblClick={() => props.onRowActivate?.(row.original)}
                  onContextMenu={(e) => props.onRowContextMenu?.(row.original, e)}
                >
                  <For each={row.getVisibleCells()}>
                    {(cell) => (
                      <td
                        data-label={
                          typeof cell.column.columnDef.header === "string"
                            ? cell.column.columnDef.header
                            : (cell.column.id ?? "")
                        }
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    )}
                  </For>
                  <Show when={props.rowActions}>
                    <td class="panel-row-actions" data-label="Actions">
                      {props.rowActions!(row.original)}
                    </td>
                  </Show>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
      <style>{`
        .data-table-container {
          display: block;
          outline: none;
        }

        .data-table-container:focus-visible {
          outline: 2px solid var(--border-focus, #4f94f4);
          outline-offset: -2px;
          border-radius: 2px;
        }

        .data-table__row {
          transition: background-color 0.1s ease;
        }

        .data-table__row--active {
          background-color: var(--bg-hover, rgba(79, 148, 244, 0.1));
        }

        .data-table-container:focus-visible .data-table__row--active {
          background-color: var(--bg-focus, rgba(79, 148, 244, 0.15));
          outline: 1px solid var(--border-focus, #4f94f4);
          outline-offset: -1px;
        }
      `}</style>
    </Show>
  );
}
